import NiceModal from '@ebay/nice-modal-react'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { useDetectClickOutside } from 'react-detect-click-outside'
import { useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import { BottomSheet } from 'react-spring-bottom-sheet'
import { toast } from 'react-toastify'

import styles from './save-button.module.scss'
import { Button } from '@/components/styleguide/Button'
import { BUTTON_HIERARCHY_PLAIN } from '@/lib/constants'
import { usePriceAlert } from '@/lib/hooks/usePriceAlert'
import { ROUTES } from '@/lib/routes'
import { selectPriceAlertList } from '@/store/selectors/locoSpace'
import { selectIsAuthenticated } from '@/store/selectors/user'
import { Size, Theme } from '@/types/system'

const AuthenticationModal = dynamic(() => import('@/components/modals/AuthenticationModal'), {
  ssr: false,
})
const CreatePriceAlertModal = dynamic(() => import('@/components/modals/CreatePriceAlertModal'), {
  ssr: false,
})
const PriceAlertMaxCapacityModal = dynamic(
  () => import('@/components/modals/PriceAlertMaxCapacityModal'),
  {
    ssr: false,
  },
)
const Icon = dynamic(() => import('@/components/styleguide/Icon'), { ssr: false })
const Dialog = dynamic(() => import('@/components/styleguide/Dialog'), { ssr: false })

export const SaveButton = ({
  includeIcon = true,
  includeText = false,
  hasBackground = false,
  selectedVehiclePrice,
  size = Size.ExtraSmall,
  hierarchy = BUTTON_HIERARCHY_PLAIN,
  className,
  theme = Theme.light,
  responsiveBreakpointInPixels = 0,
  onClick,
}) => {
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const priceAlertList = useSelector(selectPriceAlertList)
  const isMobile = useMediaQuery({ maxWidth: 992 })
  const shouldHideAllText = useMediaQuery({ maxWidth: responsiveBreakpointInPixels })
  const { doesPriceAlertExist, onRemovePriceAlert, getActivePriceAlerts } = usePriceAlert()
  const [activePriceAlert, setActivePriceAlert] = useState(false)
  const [showPriceAlertOptions, setShowPriceAlertOptions] = useState(false)

  useEffect(() => {
    const exists = doesPriceAlertExist(selectedVehiclePrice)

    setActivePriceAlert(exists)
  }, [selectedVehiclePrice, doesPriceAlertExist])

  const onCreatePriceAlert = () => {
    if (isAuthenticated) {
      if (priceAlertList.length === 10) {
        return NiceModal.show(PriceAlertMaxCapacityModal)
      }

      return NiceModal.show(CreatePriceAlertModal, { selectedVehiclePrice }).then(() => {
        getActivePriceAlerts()
        toast(
          `Successfully created your price alert for the ${selectedVehiclePrice.manufacturerName} ${selectedVehiclePrice.modelName}`,
        )
      })
    }

    return NiceModal.show(AuthenticationModal)
  }

  const onCloseModal = () => {
    setShowPriceAlertOptions(false)
  }

  const onEditPriceAlert = () => {
    setShowPriceAlertOptions(false)
    return NiceModal.show(CreatePriceAlertModal, { selectedVehiclePrice, activePriceAlert }).then(
      () => {
        getActivePriceAlerts()
        toast(
          `Successfully created your price alert for the ${selectedVehiclePrice.manufacturerName} ${selectedVehiclePrice.modelName}`,
        )
      },
    )
  }

  const onDeletePriceAlert = () => {
    setShowPriceAlertOptions(false)

    NiceModal.show(Dialog, {
      title: 'Remove price alert?',
      paragraph: 'Once removed you will permanently lose your price alert for this vehicle.',
      secondaryCtaLabel: 'Cancel',
      primaryCtaLabel: 'Remove',
      modalId: 'remove-price-alert',
    }).then(() => {
      onRemovePriceAlert(selectedVehiclePrice.vehicleId)
      setActivePriceAlert(false)
      toast(
        `Successfully removed your price alert for the ${selectedVehiclePrice.manufacturerName} ${selectedVehiclePrice.modelName}`,
      )
    })
  }

  const onButtonClick = () => {
    if (onClick) {
      onClick()
    }

    if (activePriceAlert) {
      return setShowPriceAlertOptions(show => !show)
    }

    return onCreatePriceAlert()
  }

  const ref = useDetectClickOutside({ onTriggered: () => setShowPriceAlertOptions(false) })

  const Content = () => (
    <>
      <Link href={ROUTES.priceAlertsPage.url}>
        <a className={styles.item}>
          <Icon
            name="LayoutList"
            size={Size.Large}
          />{' '}
          View price alerts
        </a>
      </Link>
      <button
        type="button"
        className={styles.item}
        onClick={onEditPriceAlert}>
        <Icon
          name="Cog"
          size={Size.Large}
        />{' '}
        Edit price alert
      </button>
      <button
        type="button"
        className={styles.item}
        onClick={onDeletePriceAlert}>
        <Icon
          name="BellMinus"
          size={Size.Large}
        />{' '}
        Remove price alert
      </button>
    </>
  )

  return (
    <div
      className={styles.container}
      ref={ref}>
      {activePriceAlert && showPriceAlertOptions && !isMobile && (
        <div className={styles.dropdown}>
          <Content />
        </div>
      )}
      {activePriceAlert && showPriceAlertOptions && isMobile && (
        <BottomSheet
          expandOnContentDrag
          open={showPriceAlertOptions}
          onDismiss={onCloseModal}
          snapPoints={({ minHeight }) => minHeight}>
          <Content />
        </BottomSheet>
      )}
      <Button
        leadingIcon={includeIcon && (activePriceAlert ? 'BellRing' : 'BellPlus')}
        trailingIcon={activePriceAlert && !isMobile ? 'ChevronUp' : ''}
        onClick={onButtonClick}
        hierarchy={hierarchy}
        size={size}
        isSquare={!includeText}
        label={
          includeText && !shouldHideAllText
            ? activePriceAlert
              ? 'Tracking prices'
              : 'Price alert'
            : ''
        }
        disabled={!selectedVehiclePrice}
        className={`${className ? className : ''}`}
        theme={theme}
        hasPadding
        hasBackground={hasBackground}
      />
    </div>
  )
}

SaveButton.propTypes = {
  includeText: PropTypes.bool,
  includeIcon: PropTypes.bool,
  selectedVehiclePrice: PropTypes.object,
  size: PropTypes.string,
  hierarchy: PropTypes.string,
  className: PropTypes.string,
  theme: PropTypes.string,
  onClick: PropTypes.func,
}
